'use client';

import { useRef, useState } from 'react';
import { consumeToolUsage } from '@/lib/browser-tool-usage';

const CM_PER_IN = 2.54;

function toFeetInches(cm: number) {
  const totalIn = cm / CM_PER_IN;
  let feet = Math.floor(totalIn / 12);
  let inches = Math.round((totalIn - feet * 12) * 10) / 10;
  if (inches >= 12) {
    feet += 1;
    inches -= 12;
  }
  return { feet, inches, totalIn };
}

const TABLE = Array.from({ length: 13 }, (_, i) => 145 + i * 5);

export default function BrowserHeightConverter() {
  const [cm, setCm] = useState('175');
  const [ft, setFt] = useState('5');
  const [inch, setInch] = useState('9');
  const reported = useRef(false);
  const report = () => {
    if (reported.current) return;
    reported.current = true;
    void consumeToolUsage('cm-to-feet-and-inches', '').catch(() => undefined);
  };

  const cmValue = Number(cm);
  const fi = Number.isFinite(cmValue) && cmValue > 0 ? toFeetInches(cmValue) : null;
  const backCm = (Number(ft) || 0) * 12 * CM_PER_IN + (Number(inch) || 0) * CM_PER_IN;

  return (
    <div className="tool-live-shell">
      <div className="tool-live-header">
        <span className="tool-live-badge">Height</span>
        <h2 className="tool-live-title">cm to feet and inches</h2>
        <p className="tool-live-subtitle">
          Convert a height in centimeters to feet and inches, or feet and inches back to cm. 1 inch
          is exactly 2.54 cm.
        </p>
      </div>
      <div className="tool-live-grid">
        <section className="tool-live-uploader">
          <label className="tool-live-field tool-live-field-full">
            <span>Centimeters</span>
            <input
              inputMode="decimal"
              onChange={(e) => {
                report();
                setCm(e.target.value);
              }}
              type="text"
              value={cm}
            />
          </label>
          <p className="tool-live-title" aria-live="polite">
            {fi
              ? `${fi.feet} ft ${fi.inches} in (${fi.feet}′${Math.round(fi.inches)}″)`
              : 'Enter a height in cm'}
          </p>
          <p className="tool-live-panel-note">
            {fi
              ? `${cm} cm = ${fi.totalIn.toFixed(2)} inches = ${(cmValue / 30.48).toFixed(3)} feet`
              : ''}
          </p>
          <div className="tool-live-field-row" style={{ display: 'flex', gap: '0.75rem' }}>
            <label className="tool-live-field">
              <span>Feet</span>
              <input
                inputMode="numeric"
                onChange={(e) => {
                  report();
                  setFt(e.target.value);
                }}
                type="text"
                value={ft}
              />
            </label>
            <label className="tool-live-field">
              <span>Inches</span>
              <input
                inputMode="decimal"
                onChange={(e) => {
                  report();
                  setInch(e.target.value);
                }}
                type="text"
                value={inch}
              />
            </label>
          </div>
          <p className="tool-live-title" aria-live="polite">
            {backCm > 0 ? `${backCm.toFixed(1)} cm` : 'Enter feet and inches'}
          </p>
        </section>
        <section className="tool-live-preview">
          <table style={{ width: '100%', fontVariantNumeric: 'tabular-nums' }}>
            <caption style={{ textAlign: 'left', padding: '0.5rem 0' }}>
              Height chart, cm to feet and inches
            </caption>
            <tbody>
              {TABLE.map((c) => {
                const r = toFeetInches(c);
                return (
                  <tr key={c}>
                    <th scope="row" style={{ textAlign: 'left', fontWeight: 400 }}>
                      {c} cm
                    </th>
                    <td style={{ textAlign: 'right' }}>
                      {r.feet} ft {r.inches} in
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </section>
      </div>
    </div>
  );
}
